import { Types as FileTypes } from '@stptech/shared-kit/build/constants/file';
import type { AxiosError } from 'axios';
import { type ClassValue, clsx } from 'clsx';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import type { GoalInterface } from '../types/Project';
import { isoDateRegex } from './RegularExpressions';

type Environment = 'PROD' | 'DEV';

export function getEnvironment(): Environment {
  const requestOrigin = window.location.origin;
  const prodSubdomainRegEx = /https?:\/\/([a-z0-9]+[.])*seinterminal[.]com/g;
  const stagingSubdomainRegEx = /https?:\/\/([a-z0-9]+[.])*sagetechnologypartners[.]com/g;

  if (prodSubdomainRegEx.test(requestOrigin) || stagingSubdomainRegEx.test(requestOrigin)) {
    return 'PROD';
  }
  return 'DEV';
}

interface ErrorResponseData {
  errors?: Array<{ message?: unknown }>;
  message?: unknown;
}

const normalizeApiMessage = (message: string): string => {
  const normalizedMessage = message.trim();
  if (!normalizedMessage) return '';

  const separatorIndex = normalizedMessage.indexOf(':');
  if (separatorIndex === -1) return normalizedMessage;

  const suffix = normalizedMessage.slice(separatorIndex + 1).trim();
  return suffix || normalizedMessage;
};

export function sanitizeResponseError(error: unknown): string {
  const axiosError = error as AxiosError<ErrorResponseData>;
  const status = axiosError?.response?.status;
  const data = axiosError?.response?.data;

  if (!axiosError?.response) {
    return 'Network request failed. Check your connection and retry. If this keeps happening, the API may be unavailable or blocked.';
  }

  if (Array.isArray(data?.errors)) {
    const errorMessages = data.errors
      .map((err) => (typeof err?.message === 'string' ? normalizeApiMessage(err.message) : ''))
      .filter(Boolean);

    if (errorMessages.length > 0) {
      return errorMessages.join(', ');
    }
  }

  if (typeof data?.message === 'string') {
    const message = normalizeApiMessage(data.message);
    if (message) return message;
  }

  if (status === 401) {
    return 'Your session has expired. Sign in again and retry.';
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (status && status >= 500) {
    return 'Server is unavailable. Retry in a moment. If this continues, contact support.';
  }

  return 'Request failed. Please retry or contact support if the problem continues.';
}

export function kFormatter(n: string) {
  const num = parseInt(n, 10);
  if (num < 1e3) return num;
  if (num >= 1e9) return `${+(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${+(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${+(num / 1e3).toFixed(2)}K`;
  return num;
}

export function kFormatterWithAbbreviation(n: string) {
  const num = parseInt(n, 10);
  if (num < 1e3) return { value: num, abbreviation: '' };
  if (num >= 1e9) return { value: +(num / 1e9).toFixed(2), abbreviation: 'B' };
  if (num >= 1e6) return { value: +(num / 1e6).toFixed(2), abbreviation: 'M' };
  if (num >= 1e3) return { value: +(num / 1e3).toFixed(2), abbreviation: 'K' };
  return { value: num, abbreviation: '' };
}

export function validatePasswordRequirement(pwd: string) {
  const hasLength = pwd.length > 8;
  const hasNumber = /[0-9]/.test(pwd);
  const hasLowercase = /[a-z]/.test(pwd);
  const hasUppercase = /[A-Z]/.test(pwd);
  const hasSpecialChar = /[!@#$%^&*()]/.test(pwd);
  const result = {
    length: hasLength,
    number: hasNumber,
    lowercase: hasLowercase,
    uppercase: hasUppercase,
    specialChar: hasSpecialChar,
    passwordMetRequirement: hasLength && hasNumber && hasLowercase && hasUppercase && hasSpecialChar,
  };
  return result;
}

export function capitalize(text: string) {
  const lowerCaseText = text.toLowerCase();
  const stringArray: string[] = lowerCaseText.split(' ').map((s) => s.charAt(0).toUpperCase() + s.slice(1));
  const capitalized = stringArray.join(' ');
  return capitalized;
}

export function formatDate(inputDate: string) {
  const dateFormat = new Date(inputDate.toString());
  let dateDisplayFormat = dateFormat.getMonth() + 1 < 10 ? `0${dateFormat.getMonth() + 1}-` : `${dateFormat.getMonth() + 1}-`;
  dateDisplayFormat += dateFormat.getDate() + 1 < 10 ? `0${dateFormat.getDate() + 1}-` : `${dateFormat.getDate() + 1}-`;
  dateDisplayFormat += dateFormat.getFullYear();
  dateDisplayFormat = new Date(dateDisplayFormat).toDateString().split(' ').slice(1).join(' ');
  dateDisplayFormat = `${dateDisplayFormat.split(' ')[0]} ${dateDisplayFormat.split(' ')[1]}, ${dateDisplayFormat.split(' ')[2]}`;
  return dateDisplayFormat;
}

export function formatDate2(inputDate: string) {
  const dateFormat = new Date(inputDate.toString());
  let dateDisplayFormat = dateFormat.getMonth() + 1 < 10 ? `0${dateFormat.getMonth() + 1}/` : `${dateFormat.getMonth() + 1}/`;
  dateDisplayFormat += dateFormat.getDate() + 1 < 10 ? `0${dateFormat.getDate()}/` : `${dateFormat.getDate()}/`;
  dateDisplayFormat += dateFormat.getFullYear();
  return dateDisplayFormat;
}

export function formatDate3(inputDate: string, format?: 'yyyy-mm-dd' | 'mm/dd/yyyy') {
  //Date Formatting from mm/dd/yyyy to yyyy-mm-dd
  //By default string entered on the Date object treats it as UTC
  // add "00:00" if not in iso format
  //Adding "00:00" will treat it as local time

  const correctedDate = isoDateRegex.test(inputDate) ? inputDate : `${inputDate} 00:00`;

  const dateFormat = new Date(correctedDate);
  //Data object get month always starts with 0
  //so, adding + 1 to correct the month
  const month = dateFormat.getMonth() + 1;
  const day = dateFormat.getUTCDate();
  const mm = month < 10 ? `0${month}` : month;
  const dd = day < 10 ? `0${day}` : day;
  const yyyy = dateFormat.getFullYear();

  if (format === 'yyyy-mm-dd') {
    return `${yyyy}-${mm}-${dd}`;
  }

  if (format === 'mm/dd/yyyy') {
    return `${mm}/${dd}/${yyyy}`;
  }

  return `${mm}/${dd}/${yyyy}`;
}

export function getLastSevenDays() {
  const sevenDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    sevenDays.push(d);
  }
  return sevenDays;
}

export function daysCount(higherDate: string, lowerDate: string) {
  const date1 = new Date(new Date(higherDate).toLocaleDateString());
  const date2 = new Date(new Date(lowerDate).toLocaleDateString());
  const timeDifference = date1.getTime() - date2.getTime();
  return Math.floor(timeDifference / (1000 * 3600 * 24));
}

export function daysCountV2(higherDate: string, lowerDate: string) {
  return Math.round(dayjs(higherDate).endOf('day').diff(dayjs(lowerDate), 'day', true));
}

export const getElapsedTime = (refDateTime: number) => {
  const refDateTimeString = new Date(refDateTime * 1000).toString();
  const today = new Date().toString();
  const timeDiff = timeDifferenceBetweenTwoDate(today, refDateTimeString);

  if (timeDiff.timeDiffDay > 0) {
    return `${timeDiff.timeDiffDay}d ago`;
  } else if (timeDiff.timeDiffHH > 0) {
    return `${timeDiff.timeDiffHH}h ago`;
  } else if (timeDiff.timeDiffMM > 0) {
    return `${timeDiff.timeDiffMM}m ago`;
  } else {
    if (timeDiff.timeDiffSS < 30) {
      return 'A moment ago';
    }
    return `${timeDiff.timeDiffSS}s ago`;
  }
};

export function formatNumber(num: number | string, locales?: string, options?: { style: string; currency: string }) {
  //locales and Options Example
  // request a currency format
  // console.log(new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(number));
  // → 123.456,79 €

  return new Intl.NumberFormat(locales, options as Intl.NumberFormatOptions).format(+num);
}

export function accountingFormatNumber({
  num,
  format = 'en-US',
  currency = 'USD',
  abbreviate,
}: {
  num: number;
  format?: string;
  currency?: string;
  abbreviate?: boolean;
} & Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(format, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency,
    ...(abbreviate && {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
  }).format(Number(num));
}

export function wordsToCamelCase(s: string) {
  //format multiple words in camel case formatting
  let formattedArray = '';
  const strArray = s.toLowerCase().split(' ');
  strArray.forEach((_w, i) => {
    if (i === 0) {
      formattedArray += strArray[i];
    } else {
      formattedArray += capitalize(strArray[i]);
    }
  });

  return formattedArray;
}

export function camelCaseToWords(s: string) {
  return s.replace(/([A-Z])/g, ' $1');
}

export function capitalizeAllWords(words: string) {
  const wordsArray = words.split(' ');
  let w = '';
  wordsArray.forEach((wrd) => {
    w += `${capitalize(wrd)} `;
  });
  return w;
}

export function getInitials(words: string) {
  const capitalizeWordsArray = capitalizeAllWords(words).split(' ');
  const FirstNameInitial = capitalizeWordsArray[0].charAt(0);
  let initials = FirstNameInitial;
  if (capitalizeWordsArray.length > 1) {
    const LastNameInitial = capitalizeWordsArray[capitalizeWordsArray.length - 2].charAt(0);
    initials += LastNameInitial;
  }
  // capitalizeWordsArray.forEach((word) => {
  //   initials += word.charAt(0);
  // });
  return initials.toUpperCase();
}

export function projectBudgetTotal(budgets: { name: string; amount: number }[]) {
  //This total needs to be store in the global state or
  //should come from backend
  //Also the object value should be amount instead of percentage
  let total = 0;

  budgets.forEach((budget) => {
    total += +budget.amount;
  });

  return total;
}

export function projectGoalsTotal(goals: GoalInterface[]): number {
  let total: number = 0;

  goals.forEach((goal: GoalInterface) => {
    total += +goal.percentage;
  });

  return total;
}

export function sortAlphabetically(val1: string, val2: string) {
  if (val1 < val2) {
    return -1;
  }
  if (val1 > val2) {
    return 1;
  }
  return 0;
}

export function randomDataSetsForTDM(value: number, count?: number) {
  const dataSets = [
    {
      data: value ? value : Math.floor(Math.random() * 10),
      color: '#0FCA98',
    },
    {
      data: value ? value : Math.floor(Math.random() * 10),
      color: '#D9B64E',
    },
    {
      data: value ? value : Math.floor(Math.random() * 10),
      color: '#F27141',
    },
    {
      data: value ? value : Math.floor(Math.random() * 10),
      color: '#38B2AC',
    },
    {
      data: value ? value : Math.floor(Math.random() * 10),
      color: '#DC3545',
    },
  ];

  if (count) {
    const newDataSets = [];
    for (let i = 0; i < count; i++) {
      newDataSets.push(dataSets[i]);
    }
    return newDataSets;
  }

  return dataSets;
}

export function transformProjectTypeChartDataSets(data: { type: string; count: number }[]) {
  const colors = [
    '#0FCA98',
    '#D9B64E',
    '#F27141',
    '#38B2AC',
    '#DC3545',
    '#2B6CB0',
    '#008080',
    '#00adad',
    '#d600d6',
    '#ffde24',
    '#bc8e52',
    '#570000',
  ];
  const dataSets: { data: number; color: string }[] = data?.map((d, i) => {
    return { data: d.count, color: colors[i] };
  });

  return dataSets;
}

export function randomDataSetsTDMTotal(datasets: { data: number; color: string }[]) {
  let total = 0;
  datasets.forEach((ds) => {
    total += ds.data;
  });

  return total;
}

export function generateRandomNumberArray(count: number, max: number) {
  const numbers = [];

  for (let i = 0; i < count; i++) {
    numbers.push(Math.floor(Math.random() * max));
  }

  return numbers;
}

export function convertEnumToText(enumText: string) {
  return capitalize(enumText.split('_').join(' '));
}

export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return `${phoneNumberString} (invalid)`;
}

export function getAcceptedFileTypes() {
  return Object.values(FileTypes).join(',');
}

export function FutureDateByDays(inputDate: any, days: number) {
  const enteredDate = new Date(inputDate);
  const futureDate = new Date(enteredDate.setDate(enteredDate.getDate() + days)).toISOString().split('T')[0];
  // var count = 0;
  // while(count < days){
  //   let futureDate = new Date(inputDate.setDate(inputDate.getDate() + 1));
  //   if(futureDate.getDay() != 0 && futureDate.getDay() != 6){
  //      //Date.getDay() gives weekday starting from 0(Sunday) to 6(Saturday)
  //      count++;
  //   }
  // }
  return futureDate;
}

// biome-ignore lint/complexity/noBannedTypes: this is a utility function
export function isObjectEmpty(obj: Object) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function extractSubdomainFromAddressBar() {
  const hostname = window.location.hostname;
  const hostnameArray = hostname.split('.');
  return hostnameArray[0];
}

export function convertEpochToDateString(epoch: number | string) {
  const myDate = new Date(Number(epoch) * 1000);
  return { date: myDate.toDateString(), time: myDate.toTimeString() };
}

export function timeDifferenceBetweenTwoDate(higherDate: string, lowerDate: string) {
  const lowerDateObj = new Date(lowerDate).getTime();
  const higherDateObj = new Date(higherDate).getTime();
  let timeDifference = higherDateObj - lowerDateObj;
  const timeDiffDay = Math.floor(timeDifference / (1000 * 3600 * 24));
  timeDifference -= timeDiffDay * 1000 * 60 * 60 * 24;
  const timeDiffHH = Math.floor(timeDifference / 1000 / 60 / 60);
  timeDifference -= timeDiffHH * 1000 * 60 * 60;
  const timeDiffMM = Math.floor(timeDifference / 1000 / 60);
  timeDifference -= timeDiffMM * 1000 * 60;
  const timeDiffSS = Math.floor(timeDifference / 1000);
  return { timeDiffDay, timeDiffHH, timeDiffMM, timeDiffSS };
}

export function constructQueryParamsUrl(
  params:
    | {
        [key: string]: string | number | boolean | undefined;
      }
    | undefined
): string {
  const paramsArray: string[] = [];

  if (params) {
    Object.keys(params).forEach((key: string, _idx: number) => {
      if (params[key]) {
        paramsArray.push(`${key}=${params[key]}`);
      }
    });
  }

  let constructedParams = '';

  if (paramsArray.length > 0) {
    constructedParams += '?';
    constructedParams += paramsArray.join('&');
  }

  return constructedParams;
}

export const containsObjectInArray = (
  arr: { [key: string]: string | number | boolean | undefined }[],
  obj: { [key: string]: string | number | boolean | undefined }
) => {
  let arrayContainsObject = false;
  if (arr) {
    arr.forEach((o) => {
      let objIsValid = true;
      if (!isObjectEmpty(o)) {
        Object.keys(o).forEach((k) => {
          objIsValid = objIsValid && o[k] === obj[k];
        });
      } else {
        objIsValid = objIsValid && isObjectEmpty(obj);
      }
      if (objIsValid) {
        arrayContainsObject = true;
      }
    });
  }
  return arrayContainsObject;
};

export const isNumberEven = (num: number) => {
  return num % 2 === 0;
};

export const sortObjectByKey = (obj: { [key: string]: any }) => {
  return Object.keys(obj)
    .sort()
    .reduce((result: { [key: string]: any }, key: string) => {
      result[key] = obj[key];
      return result;
    }, {});
};

export const countDuplicateStringsInArray = (data: string[]): { name: string; count: number }[] => {
  const duplicateStringCount: { name: string; count: number }[] = [];

  data.forEach((d) => {
    const itemIndex = duplicateStringCount.findIndex((dg) => dg.name === d);
    if (itemIndex >= 0) {
      duplicateStringCount[itemIndex] = {
        name: d,
        count: duplicateStringCount[itemIndex].count + 1,
      };
    } else {
      duplicateStringCount.push({ name: d, count: 1 });
    }
  });

  return duplicateStringCount;
};

export const compareTwoArrays = (array1: Array<string | number | boolean>, array2: Array<string | number | boolean>) => {
  const leftOverArray1 = array1.filter((a) => !array2.includes(a));
  const leftOverArray2 = array2.filter((a) => !array1.includes(a));

  return {
    array1Diff: leftOverArray1,
    array2Diff: leftOverArray2,
  };
};

export const sumNumbersArray = (numbers: Array<number | null | undefined>) => {
  let sum = 0;

  numbers.forEach((n: number | null | undefined) => {
    if (n) {
      sum += n;
    } else {
      sum += 0;
    }
  });
  return sum;
};

export const calculateDateDifference = (createdAt: Date, dueDate: Date) => {
  return dayjs(dueDate).diff(dayjs(createdAt).startOf('day'), 'day');
};

export const calculateDaysLeft = (dueDate: Date) => {
  const remainingDays = dayjs(dueDate).endOf('day').diff(dayjs(), 'day');
  return remainingDays > 0 ? remainingDays : 0;
};

export const byteConverter = (bytes: number, decimals?: number, only?: string) => {
  const K_UNIT = 1024;
  const SIZES = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  if (bytes === 0) return '0 Byte';

  if (only === 'MB') return `${(bytes / (K_UNIT * K_UNIT)).toFixed(decimals)} MB`;

  const i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  const resp = `${parseFloat((bytes / K_UNIT ** i).toFixed(decimals))} ${SIZES[i]}`;

  return resp;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCurrentYear() {
  return new Date().getFullYear();
}

export function capitalizeFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const usPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;

export function formatUsPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) {
    return digits ? `(${digits}` : '';
  }

  if (digits.length <= 6) {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3);
    return `(${area}) ${prefix}`;
  }

  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6);
  return `(${area}) ${prefix}-${line}`;
}

export function getPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function isValidEmail(email: string): boolean {
  const atIndex = email.lastIndexOf('@');
  if (atIndex <= 0) return false;

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);

  if (!local || !domain) return false;

  const labels = domain.split('.');
  if (labels.length < 2) return false;

  // Every label must be non-empty alphanumeric (hyphens allowed internally)
  if (labels.some((l) => !l || !/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(l))) return false;

  const tld = labels[labels.length - 1].toLowerCase();

  const KNOWN_TLDS = new Set([
    'com',
    'org',
    'net',
    'gov',
    'edu',
    'mil',
    'int',
    'io',
    'co',
    'ai',
    'app',
    'dev',
    'info',
    'biz',
    'name',
    'mobi',
    'pro',
    'us',
    'uk',
    'ca',
    'au',
    'nz',
    'np',
    'in',
    'de',
    'fr',
    'jp',
    'cn',
    'br',
    'mx',
    'za',
    'ng',
    'ke',
    'gh',
    'sg',
    'my',
    'ph',
    'id',
    'pk',
    'bd',
    'lk',
    'ae',
    'sa',
    'eg',
    'ma',
    'tz',
    'ug',
    'rw',
    'et',
  ]);

  // Accept if TLD is a known suffix OR domain has 3+ labels (e.g. host.city.gov)
  return KNOWN_TLDS.has(tld) || labels.length >= 3;
}
